import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Grid2 as Grid, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFinanceDetails, useFinanceSave } from '../../hooks/queries/useFinance';
import { useFinanceDefaultsPrompt } from '../../hooks/queries/useFinanceDefaultsPrompt';
import { useSnackbar } from '../../hooks/useSnackbar';
import FinanceItemsTable from './FinanceItemsTable';
import FinanceAddItemDialog from './FinanceAddItemDialog';
import FinancePromptDefaultsDialog from './FinancePromptDefaultsDialog';

export default function FinanceDetailsDialog({ open, appointmentId, onClose }) {
  const { data: details, isLoading } = useFinanceDetails(appointmentId);
  const saveMutation = useFinanceSave();
  const { shouldAsk, suppressForToday } = useFinanceDefaultsPrompt();
  const { showSuccess, showError } = useSnackbar();

  const [gross, setGross] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [items, setItems] = useState([]);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [promptState, setPromptState] = useState(null);

  useEffect(() => {
    if (details) {
      setGross(details.grossValue);
      setPaymentId(details.paymentId);
      setItems(details.financialItems || []);
    }
  }, [details, appointmentId, open]);

  const handleSave = async () => {
    try {
      const payload = {
        grossValue: gross,
        paymentId: paymentId || null,
        items,
        promoteGrossValueAsDefault: false,
        linkPaymentToSchedule: false,
      };
      const result = await saveMutation.mutateAsync({ id: appointmentId, payload });
      showSuccess('Valores salvos');

      // Pós-save: perguntas condicionais
      const askPrice = !result.serviceHasDefaultPrice
        && gross != null
        && shouldAsk('defaultPrice');

      const askPayment = paymentId
        && !result.paymentLinkedToCombo
        && shouldAsk('paymentLink');

      if (askPrice || askPayment) {
        setPromptState({ askPrice, askPayment, appointmentId });
      } else {
        onClose();
      }
    } catch (err) {
      showError(err.message || 'Erro ao salvar');
    }
  };

  const handleAddItem = (newItem) => {
    setItems([...items, newItem]);
    setAddItemOpen(false);
  };

  const handleDeleteItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Detalhes Financeiros — {details?.clientName || 'Carregando...'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                <Typography variant="body1">{details?.clientName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Telefone</Typography>
                <Typography variant="body1">{details?.clientPhone}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{details?.clientEmail || '—'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Serviço</Typography>
                <Typography variant="body1">{details?.serviceName || '—'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Profissional</Typography>
                <Typography variant="body1">{details?.professionalName || '—'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Local</Typography>
                <Typography variant="body1">{details?.placeName || '—'}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Data e Hora do Atendimento</Typography>
                <Typography variant="body1">
                  {details?.attendedAt
                    ? new Intl.DateTimeFormat('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        dateStyle: 'long',
                        timeStyle: 'short'
                      }).format(new Date(details.attendedAt))
                    : '—'}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Forma de pagamento"
                value={paymentId || ''}
                onChange={(e) => setPaymentId(e.target.value || null)}
              >
                <MenuItem value="">—</MenuItem>
                {details?.paymentOptions?.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {details?.history && details.history.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Histórico do Agendamento</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {details.history.map((event, idx) => (
                      <Box key={idx} sx={{ pb: 1, borderBottom: idx < details.history.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {new Intl.DateTimeFormat('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            dateStyle: 'short',
                            timeStyle: 'short'
                          }).format(new Date(event.timestamp || event.date))}
                        </Typography>
                        <Typography variant="body2">{event.description || event.action || event.status}</Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}


          <FinanceItemsTable
            grossValue={gross}
            onGrossChange={setGross}
            items={items}
            onItemsChange={setItems}
            onDeleteItem={handleDeleteItem}
            onAddClick={() => setAddItemOpen(true)}
            serviceName={details?.serviceName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveMutation.isPending}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <FinanceAddItemDialog
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        onSubmit={handleAddItem}
        itemsCount={items.length}
      />

      {promptState && (
        <FinancePromptDefaultsDialog
          open={!!promptState}
          askPrice={promptState.askPrice}
          askPayment={promptState.askPayment}
          grossValue={gross}
          paymentId={paymentId}
          paymentName={details?.paymentOptions?.find(p => p.id === paymentId)?.name}
          onFinish={() => {
            setPromptState(null);
            onClose();
          }}
          suppressForToday={suppressForToday}
          saveMutation={saveMutation}
          appointmentId={appointmentId}
          items={items}
        />
      )}
    </>
  );
}
